import * as React from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import Slider from '@mui/joy/Slider';
import Box from '@mui/joy/Box';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import { styled } from '@mui/joy/styles';
import Cropper, { Area } from 'react-easy-crop';

interface ImageCropperProps {
  image: string;
  open: boolean;
  /**
   * Initial aspect ratio. Users can change it or switch to freeform
   * cropping within the modal.
   */
  aspect?: number;
  onClose: () => void;
  onCropDone: (file: File) => void;
}

const CropContainer = styled('div')({
  position: 'relative',
  width: '100%',
  height: 300,
  background: '#333',
});

async function getCroppedFile(
  imageSrc: string,
  crop: Area,
): Promise<File> {
  const image: HTMLImageElement = await new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = imageSrc;
  });

  const canvas = document.createElement('canvas');
  const maxSize = 1080;
  const scale = Math.min(maxSize / crop.width, maxSize / crop.height, 1);
  canvas.width = crop.width * scale;
  canvas.height = crop.height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const file = new File([blob as Blob], 'poster.jpg', {
        type: 'image/jpeg',
      });
      resolve(file);
    }, 'image/jpeg');
  });
}

export default function ImageCropper({
  image,
  open,
  aspect = 16 / 9,
  onClose,
  onCropDone,
}: ImageCropperProps) {
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] =
    React.useState<Area | null>(null);
  const [aspectRatio, setAspectRatio] = React.useState<number | undefined>(
    aspect,
  );
  const [cropWidth, setCropWidth] = React.useState<string>('');
  const [cropHeight, setCropHeight] = React.useState<string>('');
  const [imageAspect, setImageAspect] = React.useState<number | null>(null);

  // Calculate image aspect ratio on load
  React.useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageAspect(img.width / img.height);
    };
    img.src = image;
  }, [image]);

  React.useEffect(() => {
    if (croppedAreaPixels) {
      setCropWidth(Math.round(croppedAreaPixels.width).toString());
      setCropHeight(Math.round(croppedAreaPixels.height).toString());
    }
  }, [croppedAreaPixels]);

  const handleComplete = React.useCallback(async () => {
    if (!croppedAreaPixels) return;
    const file = await getCroppedFile(image, croppedAreaPixels);
    onCropDone(file);
    onClose();
  }, [croppedAreaPixels, image, onCropDone, onClose]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog layout="center">
        <ModalClose />
        <Typography level="h4" sx={{ mb: 1 }}>
          Crop Image
        </Typography>
        <CropContainer>
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
          />
        </CropContainer>
        <Box sx={{ px: 2, mt: 2 }}>
          <Slider
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(_, value) => setZoom(value as number)}
          />
        </Box>
        <Box sx={{ px: 2, mt: 2 }}>
          <Select
            value={aspectRatio?.toString() ?? 'free'}
            onChange={(_, val) => {
              if (val === 'free') {
                setAspectRatio(undefined);
              } else if (val === 'full' && imageAspect) {
                setAspectRatio(imageAspect);
              } else {
                setAspectRatio(Number(val));
              }
            }}
          >
            <Option value="free">Free</Option>
            <Option value="full">Full</Option>
            <Option value={"1"}>1:1</Option>
            <Option value={(4 / 3).toString()}>4:3</Option>
            <Option value={(16 / 9).toString()}>16:9</Option>
          </Select>
        </Box>
        <Box sx={{ px: 2, mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <FormControl>
            <FormLabel>Width (px)</FormLabel>
            <Input
              value={cropWidth}
              onChange={(e) => setCropWidth(e.target.value)}
              type="number"
              slotProps={{ input: { min: 1, step: 1 } }}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Height (px)</FormLabel>
            <Input
              value={cropHeight}
              onChange={(e) => setCropHeight(e.target.value)}
              type="number"
              slotProps={{ input: { min: 1, step: 1 } }}
            />
          </FormControl>
        </Box>
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}
        >
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleComplete}>Upload</Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}

